/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Tag } from 'primereact/tag';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { ProjectCell } from '../../components/common/project-cell';
import { useAccessibleProjects, projectOptions } from '../../hooks/useProjectScope';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { actionMastersService, type ActionMaster } from '../../services/action-masters.service';
import { totalOf, NEWEST_FIRST, sortParams, type SortState } from '../../services/notification-engine.types';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { extractErrorMessage } from '../../utils/error-handler';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import Icon from '../../components/common/icon';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import ActiveTag from '../../components/common/active-tag';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';

/**
 * The notification engine's actions.
 *
 * Same shape as every other engine list: a Status column saying whether each
 * action is in service, and View / Edit / Delete in the row. Activating and
 * deactivating live on the action's own page beside Edit — a status change is
 * made where the thing is read, not from a row icon that looked like no other
 * list's and could only ever switch one way.
 */
const ActionMasterList = ({ embedded = false }: { embedded?: boolean }) => {
  const { navigateToCreate, navigateToView, navigateToEdit } = useSecureNavigation({
    entity: 'action', basePath: '/action-masters',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.ACTION_MASTER);
  const canUpdate = useCanUpdate(JOB_NAMES.ACTION_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.ACTION_MASTER);

  const [rows, setRows] = useState<ActionMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  // Server-side sort: the table asks, the API orders. Sorting in the browser
  // would only order the rows already fetched, which is not what a sort control
  // appears to promise. Newest first until the viewer picks a column.
  const [sort, setSort] = useState<SortState>(NEWEST_FIRST);
  const [toDelete, setToDelete] = useState<ActionMaster | null>(null);

  // A FILTER, not a field. An action's appId is stamped from the caller's token
  // when it is created (master_router_factory passes current_user.appId), so it
  // is not something the form can choose — but the list endpoint does accept an
  // appId filter, which is what this drives.
  const [appFilter, setAppFilter] = useState('');
  const [showCore, setShowCore] = useState(false);
  const { projects, canScopeByProject } = useAccessibleProjects();

  const showError = useCallback((m: string) => toast.current?.show({ severity: 'error', summary: 'Error', detail: m, life: 8000 }), []);
  const showOk = useCallback((m: string) => toast.current?.show({ severity: 'success', summary: 'Success', detail: m, life: 3000 }), []);

  const fetchRows = useCallback(async (skip = 0, limit = pageSize, sorting: SortState = sort, appId = appFilter) => {
    try {
      setLoading(true);
      const resp = await actionMastersService.list({
        skip, limit, ...sortParams(sorting), appId: appId || undefined,
        include_global: showCore,
      });
      setRows(resp.data || []);
      setTotal(totalOf(resp));
    } catch (e) {
      showError(extractErrorMessage(e, 'Failed to load actions'));
    } finally {
      setLoading(false);
    }
  }, [pageSize, sort, appFilter, showError, showCore]);

  // Mount fetch only: re-running on every fetchRows identity change would
  // reload the table on each page size change as well as on the page event.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFirst(0); fetchRows(0, pageSize); }, [showCore]);

  const handlePage = (e: any) => { setFirst(e.first); setPageSize(e.rows); fetchRows(e.first, e.rows); };

  const handleSort = (e: any) => {
    const next: SortState = { field: e.sortField || null, order: e.sortOrder || null };
    setSort(next);
    setFirst(0);
    fetchRows(0, pageSize, next);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await actionMastersService.softDelete(toDelete._id);
      setToDelete(null);
      showOk('Action deleted');
      fetchRows(first, pageSize);
    } catch (e) {
      showError(extractErrorMessage(e, 'Failed to delete action'));
    }
  };

  const columns: SaasColumn<ActionMaster>[] = useMemo(() => [
    { field: 'actionCode', header: 'Code', sortable: true, body: (r) => <span className="font-mono text-xs">{r.actionCode}</span> },
    { field: 'actionType', header: 'Type', sortable: true, body: (r) => <Tag value={r.actionType} severity="secondary" className="text-xs px-1.5 py-0.5" /> },
    {
      field: 'effectKey', header: 'Effect', sortable: true,
      body: (r) => <span className="font-mono text-xs">{r.effectKey || <span className="text-gray-400">none</span>}</span>,
    },
    { field: 'isMemoRequired', header: 'Memo', sortable: true, body: (r) => <span className="text-xs">{r.isMemoRequired ? 'Required' : '-'}</span> },
    // Whether it is in service. An inactive action is not offered to anyone, so
    // without this column a retired action and a live one looked identical.
    { field: 'isActive', header: 'Status', sortable: true, align: 'center', body: (r) => <ActiveTag row={r} /> },
    {
      field: 'appId', header: 'Project', style: { minWidth: 150 },
      body: (r: ActionMaster) => <ProjectCell appId={r.appId} projects={projects} />,
    },
    { field: 'createdAt', header: 'Created', sortable: true, body: (r) => <span className="text-xs text-gray-500 whitespace-nowrap">{r.createdAt ? formatTimestamp(r.createdAt) : '-'}</span> },
    {
      field: 'actions', header: 'Actions', align: 'center', hideable: false,
      body: (r) => (
        <div className="flex gap-1 justify-center">
          <button aria-label="View" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => navigateToView(r._id)} title="View">
            <Icon name="eye" size={17} className="text-green-600 dark:text-green-400" />
          </button>
          {canUpdate && <button aria-label="Edit" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => navigateToEdit(r._id)} title="Edit">
            <Icon name="pencil" size={17} className="text-blue-600 dark:text-blue-400" />
          </button>}
          {canDelete && <button aria-label="Delete" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setToDelete(r)} title="Delete">
            <Icon name="trash" size={17} className="text-red-600 dark:text-red-400" />
          </button>}
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canUpdate, canDelete, canScopeByProject, projects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.ACTION_MASTER}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-ActionMaster-List">
        {!embedded && (
          <PageHeader icon="pi pi-check-square" title="Actions" subtitle="What a recipient can do about a notification — defined once, attached to many" testId="ActionMaster.Header" />
        )}
        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="action-master" columns={columns} data={rows} loading={loading} dataKey="_id"
            emptyMessage="No actions found" first={first} rows={pageSize} totalRecords={total}
            onPage={handlePage} rowsPerPageOptions={[5, 10, 25, 50]} sortField={sort.field || undefined} sortOrder={sort.order} onSort={handleSort} onFilter={() => {}} testId="ActionMasterList"
            toolbarStart={
              <div className="flex items-center gap-3 flex-wrap">
                {canScopeByProject && (
                  <Dropdown
                    value={appFilter}
                    options={projectOptions(projects)}
                    optionLabel="label" optionValue="value" placeholder="All Projects"
                    showClear={!!appFilter} style={{ minWidth: 170 }}
                    onChange={(e: { value: string }) => {
                      const next = e.value || '';
                      setAppFilter(next);
                      setFirst(0);
                      fetchRows(0, pageSize, sort, next);
                    }}
                    data-testid="ActionMasterList.ProjectFilter"
                  />
                )}
                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="ActionMasterList.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={<>
              <span className="text-sm text-gray-400 whitespace-nowrap">{total} {total === 1 ? 'record' : 'records'}</span>
              <RefreshButton onClick={() => fetchRows(first, pageSize)} loading={loading} testId="ActionMasterList.Refresh" />
              {canCreate && <Button label="Create Action" icon="pi pi-plus" onClick={() => navigateToCreate()} size="small" />}
            </>}
          />
        </div>
      </div>
      <DeleteConfirmDialog visible={!!toDelete} onCancel={() => setToDelete(null)} onDelete={handleDelete}
        message={`Delete action '${toDelete?.actionCode}'?`} testId="ActionMasterList.Delete" />
    </PermissionGuard>
  );
};

export default ActionMasterList;
